import React, {useEffect} from 'react';
import {HubPathRouting} from '../../HubFramework/pathBuilder';
import {cleansePageName} from "../../Utils/transformer";
import OdxIcon from './Odx-Icon';
import {odxIconSets} from "../constants";

interface Breadcrumb {
    label: string;
    url?: string;
    isCurrent?: boolean;
}

type NativeOdxBreadcrumbProps = React.HTMLAttributes<HTMLElement> & {
    max?: number;
};

/**
 * Breadcrumbs component
 * @constructor
 */

const OdxBreadcrumbs: React.FC<NativeOdxBreadcrumbProps> = ({max, id, ...rest}) => {
    useEffect(() => {
        const updateBreadcrumbs = () => {
            setBreadcrumbs(createPathTiles());
        }

        HubPathRouting.subscribe(updateBreadcrumbs);
        updateBreadcrumbs(); // Initial call to set breadcrumbs

        return () => {
            HubPathRouting.unsubscribe(updateBreadcrumbs);
        }
    }, []);

    const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([]);

    const createPathTiles = (): Breadcrumb[] => {
        const project = HubPathRouting.currentProject;
        const module = HubPathRouting.currentModule;
        const page = HubPathRouting.currentPage;

        const tiles: Breadcrumb[] = [];

        tiles.push({ label: 'Home', url: '#/' });

        const projectLabel = project?.displayName ?? project?.id ?? '';

        if (projectLabel) {
            tiles.push({
                label: projectLabel,
                url: project.id ? `#/project/${project.id}/MyModules` : undefined
            });
        }

        if (module) {
            const label = page?.displayName ?? module.displayName ?? module.moduleName;
            if (label) {
                const projectId = project?.id;
                let moduleUrl: string | undefined;
                if (projectId && module.id && module.moduleName) {
                    const baseUrl = `#/project/${projectId}/${module.moduleName}/${module.id}`;
                    moduleUrl = page?.displayName ? `${baseUrl}/${cleansePageName(page.displayName)}` : baseUrl;
                }

                tiles.push({
                    label,
                    url: moduleUrl
                });
            }
        }

        if (tiles.length > 0) {
            tiles[tiles.length - 1] = {
                ...tiles[tiles.length - 1],
                isCurrent: true
            };
        }

        return tiles;
    };

    const calculatedMax = max ?? (breadcrumbs.length > 0 ? breadcrumbs.length : undefined);

    return (
        <div className={`!pt-6`}>
            <odx-breadcrumbs
                {...rest}
                id={id ?? 'Breadcrumbs'}
                max={calculatedMax}
            >

            {breadcrumbs.map((breadcrumb, index) => {
                const isFirst = index === 0;
                const isCurrent = breadcrumb.isCurrent ?? index === breadcrumbs.length - 1;
                const href = !isCurrent ? breadcrumb.url : undefined;
                const isDisabled = isCurrent || !href;

                return (
                    <odx-link
                        key={`${breadcrumb.label}-${index}`}
                        href={href}
                        disabled={isDisabled ? true : undefined}
                        aria-current={isCurrent ? 'page' : undefined}
                    >
                        {isFirst && (
                            <OdxIcon iconSet={odxIconSets.core} name="home" size="sm" />
                        )}
                        {breadcrumb.label}
                    </odx-link>
                );
            })}
        </odx-breadcrumbs>
        </div>
    );
};

export default OdxBreadcrumbs;
